import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useFormik } from 'formik';
import UserAddressService from '../../services/useraddress/useraddress.service';
import { useTranslation } from 'react-i18next';

interface AddAreaPincodeProps {
    showAreaPincodeDialog: boolean;
    hideAreaPincodeDialog: () => void; 
    handleAddSuccess: () => void;
}

interface SubDistrictDropdownDTO {
    subDistrictId: number;
    subDistrictName: string;
    districtName: string;
    display: string;
}

const AddingAreaPincode: React.FC<AddAreaPincodeProps> = ({
    showAreaPincodeDialog,
    hideAreaPincodeDialog,
    handleAddSuccess
}) => {
    const { t } = useTranslation();
    const [subDistricts, setSubDistricts] = useState<SubDistrictDropdownDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const toast = React.useRef<Toast>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchSubDistricts(filterValue);
        }, 300);

        return () => clearTimeout(timer);
    }, [filterValue]);

    const fetchSubDistricts = async (keyword: string = '') => {
        try {
            setLoading(true);
            const response = await UserAddressService.searchSubDistricts({ keyword });
            const formatted = response.map((item: { subDistrictName: any;}) => ({
                ...item,
                display: `${item.subDistrictName}`
            }));
            setSubDistricts(formatted);
        } catch (error) {
            showMessage('error', t('error'), t('failedToLoadSubDistricts'));
            setSubDistricts([]);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (
        severity: 'success' | 'error',
        summary: string,
        detail: string
    ) => {
        toast.current?.show({
            severity,
            summary,
            detail,
            life: 3000
        });
    };

    const formik = useFormik({
        initialValues: {  
            subDistrictId: "",
            areaName: "",
            pincode: ""
        },
        validateOnChange: false, 
        validateOnBlur: false,
        validate: (data) => {
            const errors: Record<string, string> = {};
            if (!data.subDistrictId) {
                errors.subDistrictId = t('Subdistrict Required');
            }
            if (!data.areaName) {
                errors.areaName = t('Area Name Required');
            }
            if (!data.pincode) {
                errors.pincode = t('Pincode Required');
            } else if (!/^\d{4,6}$/.test(data.pincode)) {
                errors.pincode = t('Invalid Pincode (4-6 digits)');
            }
           
            return errors;
        },
        onSubmit: async (data, { resetForm }) => {
            const areaPincodeData = {
                areaName: data.areaName,
                pincode: data.pincode,
                subDistrictId: data.subDistrictId
            };
           
            try {
                const response = await UserAddressService.addArea(areaPincodeData);
                if (response?.isSuccess) {
                    showMessage('success', t('success'), response.message);
                    handleAddSuccess();
                    hideAreaPincodeDialog();
                    resetForm();
                } else {
                     showMessage('error', t('error'), t('Area Pincode Already Exists'));
                }
            } catch (error) {
                showMessage('error', t('error'), t('error Creating Area Pincode'));
            }
        },
    });

    const handleCloseModal = () => {
        hideAreaPincodeDialog();
        formik.resetForm();
        setFilterValue('');
    };

    const dialogHeader = (
        <Button 
            className="p-button-text p-button-rounded p-button-plain"
            onClick={handleCloseModal}
            aria-label="Close"
        />
    );

    // const handleDropdownChange = (e: DropdownChangeEvent) => {
    //     if (e.value !== -1) {
    //         formik.setFieldValue("subDistrictId", e.value);
    //     }
    // };

    // const handleDropdownFilter = (e: any) => {
    //     setFilterValue(e.filter);
    //     if (e.filter.length > 2) {
    //         fetchSubDistricts(e.filter);
    //     }
    // };

    return (
        <Dialog
            visible={showAreaPincodeDialog}
            className="quickview w-50"
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
            modal
            onHide={handleCloseModal}
            closable={true}
            header={dialogHeader}
        >
            <h5 className="border-round mb-3 order-top-color p-2 panel-header text-center">
                Add New Area & Pincode
            </h5>

            <div className="flex flex-wrap">
                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="subDistrictId" className="label-semi-bold">
                            {t('Sub District')}<span className="required">*</span>
                        </label>
                        <Dropdown
                        id="stateId"
                        name="stateId"
                        value={formik.values.subDistrictId}
                        onChange={(e) => {
                            if (e.value !== null) {
                                formik.setFieldValue("subDistrictId", e.value);
                            }
                        }}
                        options={subDistricts.length > 0 ? subDistricts : [{ display: `No options available for`, subDistrictId: null }]}
                        optionLabel="display"
                        optionValue="subDistrictId"
                        placeholder={t('Search Sub-district')}
                        className={formik.errors.subDistrictId ? 'p-invalid' : ''}
                        style={{ width: '100%' }}
                        filter
                        filterBy="display"
                        showClear
                        onFilter={(e) => setFilterValue(e.filter)}
                        filterPlaceholder="Search Sub-Districts..."
                        emptyFilterMessage={''}
                        />
                        {formik.errors.subDistrictId && (
                            <small className="p-error">{formik.errors.subDistrictId}</small>
                        )}
                    </div>
                </div>
                
                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="areaName" className="label-semi-bold">
                            {t('Area Name')}<span className="required">*</span>
                        </label>
                        {/* <InputText
                            keyfilter="alphanum"
                            id="areaName"
                            name="areaName"
                            value={formik.values.areaName}
                            onChange={formik.handleChange}
                            placeholder={t('Enter Area Name')}
                            className={formik.errors.areaName ? 'p-invalid' : ''}
                            style={{ width: '100%' }}
                        /> */}
                        <InputText
                            keyfilter={/[a-zA-Z0-9 ]/}
                            id="areaName"
                            name="areaName"
                            value={formik.values.areaName}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\s+/g, ' ').trimStart();
                                formik.setFieldValue('areaName', value);
                            }}
                            placeholder={t('Enter Area Name')}
                            className={formik.errors.areaName ? 'p-invalid' : ''}
                            style={{ width: '100%' }}
                        />
                        {formik.errors.areaName && (
                            <small className="p-error">{formik.errors.areaName}</small>
                        )}
                    </div>
                </div>

                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="pincode" className="label-semi-bold">
                            {t('Pincode')}<span className="required">*</span>
                        </label>
                        <InputText
                            keyfilter="num"
                            id="pincode"
                            name="pincode"
                            value={formik.values.pincode}
                            onChange={formik.handleChange}
                            placeholder={t('Enter Pincode')}
                            className={formik.errors.pincode ? 'p-invalid' : ''}
                            style={{ width: '100%' }}
                            maxLength={6}
                        />
                        {formik.errors.pincode && (
                            <small className="p-error">{formik.errors.pincode}</small>
                        )}
                    </div>
                </div>
            </div>

            <div className="text-right mt-4">

                <Button
                    label={t('Save')}
                    onClick={() => formik.handleSubmit()}
                    className="bg-green-700 text-white"
                    disabled={loading}
                />
            </div>
            <Toast ref={toast} />
        </Dialog>
    );
};

export default AddingAreaPincode;